/**
 * Metzger Quiz - Interactive Quiz Component
 * 6 questions, skipable, with Klaviyo integration
 */

class MetzgerQuiz {
    constructor(container) {
        this.container = container;
        this.config = JSON.parse(container.querySelector('[data-quiz-config]').textContent);
        this.questions = this.config.questions;
        this.giftProducts = this.config.giftProducts;
        this.klaviyoListId = this.config.klaviyoListId;

        this.currentStep = 0;
        this.answers = {};
        this.selectedGift = null;
        this.quizPayload = null;

        this.init();
    }

    init() {
        // Check for gift restore from email CTA
        this.checkGiftRestore();

        this.renderQuestion(0);
        this.updateProgress();
    }

    /**
     * Restore cart attributes when user clicks email CTA with ?restore_gift=true
     * This ensures the Mystery Gift is re-attached even if cart was cleared
     */
    async checkGiftRestore() {
        const params = new URLSearchParams(window.location.search);

        if (params.has('restore_gift') || params.has('mystery_gift')) {
            console.log('[MetzgerQuiz] Restoring gift from email CTA');
            const restorePayload = this.buildGiftPayload({
                fromRestore: true,
                restoredBy: 'email_cta'
            });

            try {
                await fetch('/cart/update.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ attributes: restorePayload })
                });

                // Update localStorage as well
                const storagePayload = {
                    ...restorePayload,
                    title: 'Mystery Geschenk',
                    timestamp: Date.now(),
                    restored: true
                };
                localStorage.setItem('metzger_quiz_gift', JSON.stringify(storagePayload));

                // Dispatch event for selector banner
                window.dispatchEvent(new CustomEvent('metzger-quiz-complete', {
                    detail: { giftTitle: 'Mystery Geschenk', restored: true }
                }));

                // Clean URL without reload
                const cleanUrl = window.location.pathname;
                window.history.replaceState({}, '', cleanUrl);

            } catch (error) {
                console.error('[MetzgerQuiz] Gift restore failed:', error);
            }
        }
    }

    renderQuestion(index) {
        const question = this.questions[index];
        if (!question) {
            this.renderEmailCapture();
            return;
        }

        const cardEl = this.container.querySelector('.metzger-quiz__question-card');

        // Determine if it's a true/false question
        const isTrueFalse = question.type === 'truefalse';
        const answersClass = isTrueFalse ? 'metzger-quiz__answers metzger-quiz__answers--truefalse' : 'metzger-quiz__answers';

        cardEl.innerHTML = `
      <div class="metzger-quiz__question-number">Frage ${index + 1} von ${this.questions.length}</div>
      <h2 class="metzger-quiz__question-text">${question.text}</h2>
      <div class="${answersClass}">
        ${question.answers.map((answer, i) => `
          <button class="metzger-quiz__answer" data-value="${answer.value}" data-question="${question.id}">
            <span class="metzger-quiz__answer-indicator"></span>
            <span class="metzger-quiz__answer-text">${answer.label}</span>
          </button>
        `).join('')}
      </div>
      ${question.feedback ? `<div class="metzger-quiz__feedback" style="display:none;">${question.feedback}</div>` : ''}
    `;

        // Bind answer click events
        cardEl.querySelectorAll('.metzger-quiz__answer').forEach(btn => {
            btn.addEventListener('click', () => this.selectAnswer(btn));
        });

        // Render skip button
        this.renderSkipButton();
    }

    selectAnswer(btn) {
        const questionId = btn.dataset.question;
        const value = btn.dataset.value;

        // Visual feedback
        const allAnswers = btn.parentElement.querySelectorAll('.metzger-quiz__answer');
        allAnswers.forEach(a => a.classList.remove('selected'));
        btn.classList.add('selected');

        // Store answer
        this.answers[questionId] = value;
        localStorage.setItem('metzger_quiz_answers', JSON.stringify(this.answers));

        // Show feedback if exists (for true/false)
        const feedbackEl = this.container.querySelector('.metzger-quiz__feedback');
        if (feedbackEl) {
            feedbackEl.style.display = 'block';
        }

        // Move to next question after short delay
        setTimeout(() => {
            this.currentStep++;
            this.updateProgress();
            this.renderQuestion(this.currentStep);
        }, feedbackEl ? 1500 : 400);
    }

    renderSkipButton() {
        let skipBtn = this.container.querySelector('.metzger-quiz__skip');
        if (!skipBtn) {
            skipBtn = document.createElement('button');
            skipBtn.className = 'metzger-quiz__skip';
            skipBtn.textContent = 'Überspringen';
            this.container.querySelector('.metzger-quiz__question-card').after(skipBtn);
        }

        skipBtn.onclick = () => this.skipQuiz();
    }

    skipQuiz() {
        // Skip directly to email capture / gift selection
        this.renderEmailCapture();
        this.updateProgress(this.questions.length);
    }

    updateProgress(forceStep = null) {
        const step = forceStep !== null ? forceStep : this.currentStep;
        const progressSteps = this.container.querySelectorAll('.metzger-quiz__progress-step');

        progressSteps.forEach((el, i) => {
            el.classList.remove('active', 'completed');
            if (i < step) {
                el.classList.add('completed');
            } else if (i === step) {
                el.classList.add('active');
            }
        });
    }

    determineGift() {
        // Logic: Based on answers, select appropriate gift
        // Default to random gift from pool

        const gifts = this.giftProducts;
        if (!gifts || gifts.length === 0) return null;

        // Simple matching logic - can be expanded
        // Priority: Match based on meat preference (q4)
        const meatPref = this.answers.q4;

        let matchedGift = null;

        if (meatPref === 'rind') {
            matchedGift = gifts.find(g => g.category === 'rind');
        } else if (meatPref === 'gefluegel') {
            matchedGift = gifts.find(g => g.category === 'gefluegel');
        }

        // Fallback to first gift
        return matchedGift || gifts[0];
    }

    buildGiftPayload(overrides = {}) {
        const selectedGiftTitle = this.selectedGift && this.selectedGift.title ? this.selectedGift.title : 'Mystery Geschenk';
        const selectedGiftHandle = this.selectedGift && this.selectedGift.handle ? this.selectedGift.handle : '';
        const selectedGiftVariant = this.selectedGift && this.selectedGift.variantId ? this.selectedGift.variantId : '';
        const selectedGiftCategory = this.selectedGift && this.selectedGift.category ? this.selectedGift.category : '';
        const quizAnswersDigest = JSON.stringify(this.answers || {});

        return {
            'first_order_gift': 'true',
            'quiz_completed': 'true',
            'quiz_gift_title': selectedGiftTitle,
            'quiz_gift_recommendation': selectedGiftTitle,
            'quiz_gift_handle': selectedGiftHandle,
            'quiz_gift_variant': String(selectedGiftVariant),
            'quiz_gift_category': selectedGiftCategory,
            'quiz_answers_digest': quizAnswersDigest,
            'mystery_gift': 'true',
            'staff_order_summary': 'ERSTE BESTELLUNG - MYSTERY GESCHENK BEILEGEN',
            'quiz_completed_at': new Date().toISOString(),
            ...overrides
        };
    }

    renderEmailCapture() {
        // Hide skip button
        const skipBtn = this.container.querySelector('.metzger-quiz__skip');
        if (skipBtn) skipBtn.style.display = 'none';

        // Determine gift internally (for staff notifications)
        this.selectedGift = this.determineGift();

        const cardEl = this.container.querySelector('.metzger-quiz__question-card');

        // Show MYSTERY GESCHENK to customer (not the actual product)
        cardEl.innerHTML = `
      <div class="metzger-quiz__email-capture">
        <div class="metzger-quiz__gift-preview">
          <span class="metzger-quiz__gift-badge">🎁 Dein Geschenk</span>
          <div style="font-size: 4rem; margin: 1.5rem 0;">🎁✨</div>
          <h3 class="metzger-quiz__gift-title">Mystery Geschenk</h3>
          <p class="metzger-quiz__gift-subtitle">Lass dich überraschen! Gratis zu deiner ersten Metzgerpaket-Bestellung.</p>
        </div>
        
        <form class="metzger-quiz__email-form" id="quiz-email-form">
          <input type="email" class="metzger-quiz__email-input" name="email" placeholder="Deine E-Mail Adresse" required>
          <button type="submit" class="metzger-quiz__submit-btn">Mystery Geschenk sichern →</button>
        </form>
        
        <p class="metzger-quiz__disclaimer">
          Mit deiner Anmeldung erhältst du dein Gratis-Geschenk und unseren Newsletter. 
          Jederzeit abmeldbar. <a href="/policies/privacy-policy">Datenschutzerklärung</a>
        </p>
      </div>
    `;

        // Bind form submit
        const form = cardEl.querySelector('#quiz-email-form');
        form.addEventListener('submit', (e) => this.handleEmailSubmit(e));
    }

    async handleEmailSubmit(e) {
        e.preventDefault();

        const form = e.target;
        const email = form.elements.email.value;
        const submitBtn = form.querySelector('.metzger-quiz__submit-btn');
        const originalText = submitBtn.textContent;

        submitBtn.textContent = 'Wird verarbeitet...';
        submitBtn.disabled = true;

        try {
            // 1. Send to Klaviyo with quiz answers as properties
            await this.sendToKlaviyo(email);

            // 2. Save gift to cart attributes
            await this.saveGiftToCart();

            // 3. Show success and redirect
            this.renderSuccess();

        } catch (error) {
            console.error('Quiz submission error:', error);
            submitBtn.textContent = 'Fehler - Nochmal versuchen';
            submitBtn.disabled = false;
            setTimeout(() => {
                submitBtn.textContent = originalText;
            }, 2000);
        }
    }

    async sendToKlaviyo(email) {
        const params = new URLSearchParams();
        params.append('g', this.klaviyoListId);
        params.append('email', email);

        // Add quiz answers as properties
        Object.keys(this.answers).forEach(key => {
            params.append(`quiz_${key}`, this.answers[key]);
        });

        // Add recommended gift
        if (this.selectedGift) {
            params.append('quiz_recommended_gift', this.selectedGift.title);
            params.append('quiz_gift_handle', this.selectedGift.handle);
        }

        params.append('quiz_completed', 'true');
        params.append('$source', 'Metzgerpaket Quiz');

        // Also identify via Klaviyo JS API for better tracking
        if (window._learnq) {
            const properties = {
                '$email': email,
                'quiz_completed': true,
                'quiz_answers': this.answers,
                // Individual properties for Klaviyo analytics
                'quiz_motivation': this.answers.q1 || '',
                'quiz_household_size': this.answers.q2 || '',
                'quiz_usage': this.answers.q3 || '',
                'quiz_meat_preference': this.answers.q4 || '',
                'quiz_trust_answer': this.answers.q5 || '',
                'quiz_price_priority': this.answers.q6 || '',
                'quiz_recommended_gift': this.selectedGift ? this.selectedGift.title : ''
            };

            window._learnq.push(['identify', properties]);
            window._learnq.push(['track', 'Quiz Completed', properties]);
        }

        const response = await fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', {
            method: 'POST',
            headers: {
                'content-type': 'application/x-www-form-urlencoded',
                'cache-control': 'no-cache'
            },
            body: params
        });

        const data = await response.json();
        if (!data.success) {
            throw new Error(data.errors ? JSON.stringify(data.errors) : 'Klaviyo subscription failed');
        }

        return data;
    }

    async saveGiftToCart() {
        // Simply mark this as a first-order gift
        // Staff will choose the mystery gift themselves
        const payload = this.buildGiftPayload();
        this.quizPayload = payload;

        await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: payload })
        });

        // Store in localStorage for selector banner
        localStorage.setItem('metzger_quiz_gift', JSON.stringify({
            title: payload.quiz_gift_title,
            variantId: payload.quiz_gift_variant,
            handle: payload.quiz_gift_handle,
            category: payload.quiz_gift_category,
            timestamp: Date.now(),
            quiz_answers_digest: payload.quiz_answers_digest,
            staff_order_summary: payload.staff_order_summary
        }));

        // Dispatch custom event so selector can show banner immediately
        window.dispatchEvent(new CustomEvent('metzger-quiz-complete', {
            detail: { giftTitle: 'Mystery Geschenk' }
        }));
    }

    renderSuccess() {
        const cardEl = this.container.querySelector('.metzger-quiz__question-card');

        cardEl.innerHTML = `
      <div class="metzger-quiz__result">
        <div class="metzger-quiz__result-icon">🎉</div>
        <h2 class="metzger-quiz__result-title">Perfekt!</h2>
        <p class="metzger-quiz__result-message">
          Dein Geschenk wartet auf dich. Wähle jetzt dein Metzgerpaket und wir legen es zur ersten Lieferung dazu!
        </p>
        <a href="#metzger-abo-selector" class="metzger-quiz__continue-btn" onclick="document.querySelector('#metzger-abo-selector')?.scrollIntoView({behavior:'smooth'})">
          Jetzt Metzgerpaket wählen →
        </a>
      </div>
    `;

        // Update progress to complete
        this.updateProgress(this.questions.length + 1);
    }
}

// Initialize when DOM ready
document.addEventListener('DOMContentLoaded', () => {
    const quizContainer = document.querySelector('.metzger-quiz');
    if (quizContainer) {
        new MetzgerQuiz(quizContainer);
    }
});
